import { zodResolver } from '@hookform/resolvers/zod';
import { Flex } from 'antd';
import isEqual from 'lodash/isEqual';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { ConditionView, FormSelect, FormTextInput } from '@/shared/ui';

import { FormConstants } from '../../../../components/Forms/constants';
import {
  internationalAddressBlockBaseSchemaWithoutAppartments,
  internationalAddressBlockSchema,
} from '../model/schemas';
import { InternationalAddressBlockSchema } from '../model/types';

const defaultValues: Record<keyof InternationalAddressBlockSchema, null> = {
  region: null,
  district: null,
  settlement: null,
  street: null,
  custom_street: null,
  house: null,
  building: null,
  apartment: null,
  zip_code: null,
};

interface Props {
  address: InternationalAddressBlockSchema;
  hideAppartments?: boolean;
  revalidate: boolean;
  handleAddressChange: (formData: InternationalAddressBlockSchema) => void;
}

export const InternationalAddressBlock: React.FC<Props> = ({
  address,
  revalidate,
  hideAppartments = false,
  handleAddressChange,
}) => {
  const { control, getValues, trigger, setValue } = useForm<InternationalAddressBlockSchema>({
    values: address,
    resolver: zodResolver(
      hideAppartments ? internationalAddressBlockBaseSchemaWithoutAppartments : internationalAddressBlockSchema,
    ),
  });

  useEffect(() => {
    revalidate && trigger();
  }, [revalidate]);

  useEffect(() => {
    if (isEqual(address, getValues())) return;

    Object.keys(internationalAddressBlockSchema.shape).forEach((fieldName) => {
      const typedFieldName = fieldName as keyof InternationalAddressBlockSchema;

      setValue(typedFieldName, address[typedFieldName] ?? defaultValues[typedFieldName]);
    });
  }, [address]);

  const handleChange = (): void => {
    revalidate && trigger();
    handleAddressChange(getValues());
  };

  return (
    <>
      <FormSelect name="region" control={control} label="Область" options={[]} disabled />
      <FormSelect name="district" control={control} label="Район" options={[]} disabled />

      <FormTextInput
        name="settlement"
        control={control}
        label="Населений пункт"
        onChange={handleChange}
        maxLength={FormConstants.INPUT_MAX_LENGTH_100}
      />
      <FormTextInput
        name="street"
        control={control}
        label="Вулиця"
        onChange={handleChange}
        maxLength={FormConstants.INPUT_MAX_LENGTH_100}
      />

      <ConditionView if={!hideAppartments}>
        <Flex gap={16}>
          <FormTextInput
            name="house"
            control={control}
            label="Номер будинку"
            onChange={handleChange}
            maxLength={FormConstants.INPUT_MAX_LENGTH_7}
          />
          <FormTextInput
            name="building"
            control={control}
            label="Номер корпусу"
            onChange={handleChange}
            maxLength={FormConstants.INPUT_MAX_LENGTH_5}
          />
          <FormTextInput
            name="apartment"
            control={control}
            label="Номер квартири"
            onChange={handleChange}
            maxLength={FormConstants.INPUT_MAX_LENGTH_5}
          />
        </Flex>
      </ConditionView>
    </>
  );
};
